import { Session } from "@wingspanhq/wingspan-api-sdk";

const REFRESH_TOKEN_STORAGE_KEY = "ws:v3:refresh_token";
const PREFERRED_ACCOUNT_KEY_PREFIX = "ws:v3:preferred_account:";

export type SessionIdentity = {
  sessionId: string;
  personId?: string;
  accountId?: string;
};

class TokenStore {
  private accessToken: string | undefined;
  private identity: SessionIdentity | undefined;
  private persisted = false;
  private listeners = new Set<() => void>();

  private storageFor(persist: boolean): Storage {
    return persist ? window.localStorage : window.sessionStorage;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    this.listeners.forEach(listener => listener());
  }

  getAccessToken(): string | undefined {
    return this.accessToken;
  }

  getIdentity(): SessionIdentity | undefined {
    return this.identity;
  }

  getRefreshToken(): string | undefined {
    const fromLocal = window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (fromLocal) {
      this.persisted = true;
      return fromLocal;
    }

    const fromSession = window.sessionStorage.getItem(
      REFRESH_TOKEN_STORAGE_KEY
    );
    if (fromSession) {
      this.persisted = false;
      return fromSession;
    }

    return undefined;
  }

  setSession(session: Session, persist?: boolean): void {
    if (typeof persist === "boolean") {
      this.persisted = persist;
    }

    this.accessToken = session.token;
    this.identity = {
      sessionId: session.id,
      personId: session.personId ?? undefined,
      accountId: session.boundAccountId ?? undefined
    };

    if (session.refreshToken) {
      window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      window.sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      this.storageFor(this.persisted).setItem(
        REFRESH_TOKEN_STORAGE_KEY,
        session.refreshToken
      );
    }

    this.notify();
  }

  clear(): void {
    this.accessToken = undefined;
    this.identity = undefined;
    window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    window.sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);

    this.notify();
  }

  getPreferredAccountId(personId: string): string | undefined {
    return (
      window.localStorage.getItem(PREFERRED_ACCOUNT_KEY_PREFIX + personId) ??
      undefined
    );
  }

  setPreferredAccountId(personId: string, accountId: string): void {
    window.localStorage.setItem(
      PREFERRED_ACCOUNT_KEY_PREFIX + personId,
      accountId
    );
  }

  clearPreferredAccountId(personId: string): void {
    window.localStorage.removeItem(PREFERRED_ACCOUNT_KEY_PREFIX + personId);
  }
}

export const tokenStore = new TokenStore();
